import {
    PaymentPaymentMethodsResponse,
    SubscriptionSubscriptionChangePreview,
    SubscriptionSubscriptionCostPreview,
    SubscriptionSubscriptionData,
} from '@/backend-api/Api';

import { CreateSubscriptionsResponse, ReportGroupsResponse } from './types';

export interface SubscriptionsListResponse {
    count: number;
    subscriptions: SubscriptionSubscriptionData[];
}

export async function fetchSubscriptions(): Promise<SubscriptionsListResponse> {
    const response = await fetch('/app-api/subscriptions');
    if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
    }
    return response.json();
}

export async function fetchReportGroups(includeTrials = false): Promise<ReportGroupsResponse> {
    const url = includeTrials ? '/app-api/market-reports/groups?includeTrials=true' : '/app-api/market-reports/groups';
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Failed to fetch report groups');
    }
    return response.json();
}

export async function fetchSubscription(subscriptionId: number): Promise<SubscriptionSubscriptionData> {
    const response = await fetch(`/app-api/subscriptions/${subscriptionId}`);
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch subscription' }));
        throw new Error(error.message || 'Failed to fetch subscription');
    }
    return response.json();
}

export async function cancelSubscription(subscriptionId: number, immediately: boolean = false): Promise<void> {
    const response = await fetch(`/app-api/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            immediately,
            reason: 'User requested cancellation from cart page',
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to cancel subscription');
    }
}

export async function reactivateSubscription(subscriptionId: number): Promise<void> {
    const response = await fetch(`/app-api/subscriptions/${subscriptionId}/reactivate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to reactivate subscription' }));
        throw new Error(error.message || 'Failed to reactivate subscription');
    }
}

export async function updateSubscription(
    subscriptionId: number,
    items: Array<{ priceId: number; priceType: 'group' }>
): Promise<void> {
    const response = await fetch(`/app-api/subscriptions/${subscriptionId}/confirm-change`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            items,
            note: 'User updated subscription from cart page',
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update subscription' }));
        throw new Error(error.message || 'Failed to update subscription');
    }
}

export async function fetchPaymentMethods(): Promise<PaymentPaymentMethodsResponse> {
    const response = await fetch('/app-api/payment-methods');
    if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
    }
    return response.json();
}

export async function createSetupIntent(): Promise<{ clientSecret: string }> {
    const response = await fetch('/app-api/payment-methods/setup-intent', {
        method: 'POST',
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create setup intent');
    }
    return response.json();
}

export async function createSubscriptionsWithPayment(params: {
    items: Array<{ priceId: number; priceType: 'group' }>;
    paymentMethodId: string;
}): Promise<CreateSubscriptionsResponse> {
    const response = await fetch('/app-api/subscriptions/create-with-payment', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            items: params.items,
            paymentMethodId: params.paymentMethodId,
        }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || 'Failed to create subscriptions';
        throw new Error(errorMessage);
    }

    return response.json();
}

export interface SignupForCheckoutResponse {
    success: boolean;
    user: {
        id: string;
        email: string;
    };
}

export async function createPlatformSubscriptionWithPayment(
    templateId: number,
    paymentMethodId: string
): Promise<CreateSubscriptionsResponse> {
    const response = await fetch(`/app-api/subscriptions/platform-checkout-with-payment/${templateId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethodId }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || 'Failed to create subscription';
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function signupForCheckout(email: string): Promise<SignupForCheckoutResponse> {
    const response = await fetch('/app-api/accounts/signup-for-checkout', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || 'Failed to create account';
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function previewSubscriptionChange(
    subscriptionId: number,
    items: Array<{ priceId: number; priceType: 'group' }>
): Promise<SubscriptionSubscriptionChangePreview> {
    const response = await fetch(`/app-api/subscriptions/${subscriptionId}/preview-change`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            items,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to preview subscription change' }));
        throw new Error(error.message || 'Failed to preview subscription change');
    }

    return response.json();
}

export async function previewSubscriptionCosts(
    items: Array<{ priceId: number; priceType: 'group' | 'platform' }>
): Promise<SubscriptionSubscriptionCostPreview> {
    const response = await fetch('/app-api/subscriptions/preview-costs', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            items,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to preview subscription costs' }));
        throw new Error(error.message || 'Failed to preview subscription costs');
    }

    return response.json();
}
